import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'

export type MobileTabBarItem = {
  key: string
  label: string
  href?: string
  icon: ReactNode | ((props: { color: string }) => ReactNode)
}

export type MobileTabBarProps = {
  items: MobileTabBarItem[]
  activeKey?: string
  onChange?: (key: string) => void
  className?: string
  renderInPortal?: boolean
}

const labelTypography = designTokens.typography.mobile.caption2Semibold
const labelStyle = typographyStyle(labelTypography, {
  fontFamily: designTokens.typography.fontFamilies.mobileSystem,
  letterSpacing: labelTypography.letterSpacing,
})

const itemStyle = {
  gap: designTokens.spacing.mobileTabBarGap,
  paddingTop: designTokens.spacing.mobileTabBarItemPaddingTop,
  paddingBottom: designTokens.spacing.mobileTabBarItemPaddingBottom,
}

const tabBarStyle = {
  background: designTokens.colors.background.mobileTabBar,
  borderTop: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.mobileTabBarDivider}`,
  boxShadow: designTokens.shadows.mobileCard,
  paddingBottom: designTokens.spacing.mobileTabBarBottomPadding,
}

const renderIcon = (icon: MobileTabBarItem['icon'], color: string) => {
  if (typeof icon === 'function') {
    return icon({ color })
  }
  return icon
}

export function MobileTabBar({
  items,
  activeKey,
  onChange,
  className,
  renderInPortal = true,
}: MobileTabBarProps) {
  const content = (
    <nav className={cn('fixed bottom-0 left-0 right-0 z-50', className)} style={tabBarStyle}>
      <div className="flex w-full items-center justify-between">
        {items.map((item) => {
          const isActive = item.key === activeKey
          const color = isActive
            ? designTokens.colors.brand.primary
            : designTokens.colors.text.primary

          const itemContent = (
            <div className="flex flex-1 flex-col items-center justify-center" style={itemStyle}>
              {renderIcon(item.icon, color)}
              <span style={{ ...labelStyle, color }}>{item.label}</span>
            </div>
          )

          if (item.href) {
            return (
              <Link
                key={item.key}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                className="flex flex-1"
                onClick={() => onChange?.(item.key)}
              >
                {itemContent}
              </Link>
            )
          }

          return (
            <button
              key={item.key}
              type="button"
              className="flex flex-1"
              onClick={() => onChange?.(item.key)}
              aria-current={isActive ? 'page' : undefined}
            >
              {itemContent}
            </button>
          )
        })}
      </div>
    </nav>
  )

  if (!renderInPortal) {
    return content
  }

  if (typeof document === 'undefined') {
    return null
  }

  return createPortal(content, document.body)
}
